// Parsers that turn a personality's free-text exercise `prompt` string (authored
// in src/utils/category_mappings.tsx) into the structured inputs each interactive
// exercise type needs (ClickUp 86badfk6e — exercises 2–5).
//
// WHY PARSE AT ALL: the content was written as prose describing the intended
// interaction (e.g. "Drag-and-drop circles: Family, Legacy, Security…",
// "Rank these in order… Options: Innovation, Growth, Autonomy",
// "New habit: ____, Why it matters: ____", "3-day log: What did I buy?…").
// The interaction *type* is consistent per exercise id across all personalities
// (vision doc §5), but the *content* (the item list / field labels / questions)
// lives only in these strings. We extract it where the shape is reliable and
// fall back to sensible defaults where a given personality's string diverges —
// never throwing, so a malformed/duplicated prompt (there is one known
// copy-paste content bug where a personality repeats Ex 1's text for Ex 2/3/4)
// degrades to a usable generic UI instead of breaking the workbook.

// ---------------------------------------------------------------------------
// Exercise 2 — Visualization / mapping (place-and-label board)
// ---------------------------------------------------------------------------
// Most prompts are "Drag-and-drop <thing>: a, b, c, d, e. Label each with…".
// A few diverge (quadrant grid / radar / pyramid / image collage). We extract
// the comma list when present; otherwise the board starts empty and the user
// adds their own items (the "+ add your own" affordance), seeded by the prompt
// shown above the board. Either way the UI is one consistent labeled board.

/** Suggested draggable item chips parsed from an Exercise-2 prompt. */
export function parseMapItems(prompt: string): string[] {
  if (!prompt) return [];

  // Some prompts carry the item list inside a parenthetical ("(e.g., shield,
  // house, lock, piggy bank, calendar)") rather than after a colon. Prefer the
  // parenthetical when it looks like a comma list; otherwise use the text after
  // the first colon (the "Drag-and-drop circles: a, b, c" form).
  let listPart = '';
  // Prefer a parenthetical that holds the actual comma-list — content uses both
  // "(e.g., shield, house, lock)" AND a plain "(fashion, dining, fitness, …)"
  // (the quadrant prompt's real items live in the parenthetical, while the text
  // before the colon is just axis labels). Scan ALL parentheticals and take the
  // FIRST one that is actually a comma-list, so a non-list parenthetical
  // appearing earlier doesn't shadow the real one. Strip a leading "e.g.,".
  const parenMatches = prompt.match(/\(([^)]+)\)/g) || [];
  const commaListParen = parenMatches
    .map((p) => p.slice(1, -1)) // drop the surrounding ()
    .find((inner) => inner.includes(','));
  if (commaListParen) {
    listPart = commaListParen.replace(/^\s*e\.?g\.?,?\s*/i, '');
  } else {
    const afterColon = prompt.split(/:(.+)/s)[1] ?? '';
    // Cut off the instructional trailer that follows the item list.
    listPart = afterColon
      .split(
        /\b(?:Label each|Users? (?:place|shade|fill|plot)|Place |Prompts?:|Add labels|and label|Plot )/i
      )[0]
      .trim();
  }
  if (!listPart) return [];

  const items = listPart
    // split on commas, "etc.", and " vs. " (quadrant prompts use vs.)
    .split(/,|\betc\.?\b|\bvs\.?\b/i)
    .map((s) =>
      s
        // drop any remaining parenthetical qualifiers and stray quotes
        .replace(/\(.*?\)/g, '')
        .replace(/["“”]/g, '')
        // strip leading/trailing punctuation (e.g. a trailing period on the
        // last list item: "Career.")
        .replace(/^[\s.;:]+|[\s.;:]+$/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    )
    // keep short label-like tokens; drop sentence fragments / empties
    .filter((s) => s.length > 0 && s.length <= 40 && !/[?]/.test(s));

  // De-dupe (case-insensitive) and cap so a runaway parse can't flood the UI.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of items) {
    const k = it.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(it);
    }
  }
  return out.slice(0, 8);
}

// ---------------------------------------------------------------------------
// Exercise 3 — Action planning (structured form)
// ---------------------------------------------------------------------------
// Prompts are a list of "Label: __________" pairs (field count varies per
// personality, and some include a "Start date: [Pick a date]"). We split into
// labeled fields; any field whose label mentions a date renders as a date input.

export interface ActionPlanField {
  key: string; // stable storage key derived from the label
  label: string; // human label shown above the input
  isDate: boolean; // render a date picker instead of a text box
}

const DATE_LABEL = /\b(date|when will|target date|pick a date|start)\b/i;

/** Slugify a label into a stable storage key. */
function slugify(label: string): string {
  return (
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'field'
  );
}

export function parseActionPlanFields(prompt: string): ActionPlanField[] {
  if (!prompt) return defaultActionPlanFields();

  // Content writes fields as "Label: __________" pairs, but several prompts run
  // two labels into one blank-delimited segment via an inner colon
  // ("Budget…: Criteria for evaluating new ideas: ____"). Normalize the blank
  // markers, drop the "[Pick a date]" placeholder (its label already says
  // "date"), then split on BOTH the blank sentinel and inner colons so each
  // label becomes its own field. The label of a field is the trailing
  // phrase before its blank, after stripping any preceding field's value text.
  const normalized = prompt
    .replace(/\[pick a date\]/gi, '') // label carries the date intent already
    .replace(/_{2,}/g, '␟') // ␟ = a blank sentinel
    .replace(/:/g, '␟'); // treat every colon as a field boundary too

  const chunks = normalized
    .split('␟')
    .map((c) => c.trim())
    .filter(Boolean);

  const fields: ActionPlanField[] = [];
  // Track how many times each slug has appeared so repeated labels (e.g. a
  // prompt with "Lever 1: __ How I use it: __, Lever 2: __ How I use it: __")
  // become DISTINCT fields rather than collapsing into one input.
  const slugCounts: Record<string, number> = {};
  for (let raw of chunks) {
    // The label is the trailing phrase of this chunk (a prior field's value
    // text may precede it after a comma). Split on commas only; a trailing "?"
    // is part of the question label ("What must be true before I commit?"), so
    // keep it. Drop a leading clause that ends in "?" + space (value text of a
    // prior question-field that ran into this one).
    const parts = raw.split(',');
    let label = parts[parts.length - 1].trim();
    // If the remaining label still contains an internal "? " boundary, keep the
    // text after the last such boundary (the actual label of this field).
    const qParts = label.split(/\?\s+/);
    if (qParts.length > 1) label = qParts[qParts.length - 1];
    label = label.replace(/["“”]/g, '').replace(/^[.\s]+|[.\s]+$/g, '').trim();
    if (!label || label.length < 3 || label.length > 90) continue;
    const baseKey = slugify(label);
    const n = (slugCounts[baseKey] = (slugCounts[baseKey] || 0) + 1);
    // First occurrence keeps the bare slug; repeats get a numeric suffix so the
    // storage key (and React key) stays unique. The visible label is unchanged.
    const key = n === 1 ? baseKey : `${baseKey}_${n}`;
    fields.push({ key, label, isDate: DATE_LABEL.test(label) });
  }

  if (fields.length === 0) return defaultActionPlanFields();
  return fields.slice(0, 8);
}

/** Fallback fields matching the scope's canonical action-plan shape. */
export function defaultActionPlanFields(): ActionPlanField[] {
  return [
    { key: 'new_habit', label: 'New habit I want to try', isDate: false },
    { key: 'why_it_matters', label: 'Why it matters', isDate: false },
    { key: 'how_to_track', label: 'How I will track progress', isDate: false },
    { key: 'start_date', label: 'Start date', isDate: true },
  ];
}

// ---------------------------------------------------------------------------
// Exercise 4 — Behavior pattern tracker (≈3-day daily log)
// ---------------------------------------------------------------------------
// Prompts are "3-day log: <q1>? <q2>? <q3>?" — a small set of per-day questions
// the user answers each day. We extract the question fields; the day count is
// fixed at 3 (the consistent design across personalities).

export const TRACKER_DAYS = 3;

export interface TrackerField {
  key: string;
  label: string;
}

export function parseTrackerFields(prompt: string): TrackerField[] {
  if (!prompt) return defaultTrackerFields();

  // Drop the "3-day log:" / "Daily log entry for 3 days:" lead-in and any blank
  // sentinels ("__________") the content embeds, then split the remaining
  // questions on "?" (most are questions) falling back to commas.
  const body = prompt
    .replace(/^[^:]*log[^:]*:/i, '')
    .replace(/_{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  let parts: string[];
  if (body.includes('?')) {
    parts = body.split('?').map((s) => s.trim());
  } else {
    parts = body.split(/,|\./).map((s) => s.trim());
  }

  const fields: TrackerField[] = [];
  const seen = new Set<string>();
  for (let p of parts) {
    let label = p.replace(/["“”]/g, '').replace(/\s+/g, ' ').trim();
    if (!label) continue;
    if (label.length > 110) continue; // skip stray sentence runs
    const key = slugify(label);
    if (seen.has(key)) continue;
    seen.add(key);
    fields.push({ key, label: body.includes('?') ? `${label}?` : label });
  }

  if (fields.length === 0) return defaultTrackerFields();
  return fields.slice(0, 5);
}

export function defaultTrackerFields(): TrackerField[] {
  return [
    { key: 'what_happened', label: 'What happened today?' },
    { key: 'how_it_felt', label: 'How did it feel?' },
    { key: 'would_repeat', label: 'Would I make the same choice again?' },
  ];
}

// ---------------------------------------------------------------------------
// Exercise 5 — Insight ranking (drag-and-drop ordering)
// ---------------------------------------------------------------------------
// Prompts are reliably "Rank these… Options: A, B, C, D, E, F". We extract the
// options after "Options:" and let the user drag them into priority order.

export function parseRankingOptions(prompt: string): string[] {
  if (!prompt) return [];
  // REQUIRE the "Options:" marker. Every authored ranking prompt has it; the
  // few id-5 slots that DON'T (a misplaced image-collage prompt + one known
  // content copy-paste bug) must NOT be parsed as a ranking — doing so renders
  // sentence fragments as draggable chips. Return empty so RankingBody shows its
  // "no options to rank" state instead of garbage, and warn in dev so the
  // content bug is visible during the McKayla reconcile (86badfkb1).
  const m = prompt.match(/Options?:\s*(.+)$/is);
  if (!m) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[exercisePrompts] ranking prompt missing "Options:" marker — not parseable as a ranking:',
        prompt
      );
    }
    return [];
  }
  const listPart = m[1].trim();

  const opts = listPart
    // content uses commas, and one personality has a stray period separator
    // ("Stability. Accountability, …") — split on both.
    .split(/,|\.(?=\s|$)/)
    .map((s) =>
      s
        .replace(/["“”\\]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter((s) => s.length > 0 && s.length <= 40);

  const seen = new Set<string>();
  const out: string[] = [];
  for (const o of opts) {
    const k = o.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(o);
    }
  }
  return out.slice(0, 10);
}
